import React, {useCallback, useEffect, useState} from "react";
import {listOfficeLeads, setOfficeLeadStatus} from "../api/admin";

const FILTERS = ["NEW", "CONTACTED", "CLOSED", "ALL"];
const TONE = {NEW: "amber", CONTACTED: "green", CLOSED: "grey"};
const KIND = {ENQUIRY: "Enquiry", BROCHURE: "Brochure"};

const when = (iso) => (iso ? new Date(iso).toLocaleString([], {dateStyle: "medium", timeStyle: "short"}) : "");

/** Enquiries and brochure requests on listings with no broker: nobody else sees these, so OGM answers them here. */
const OfficeLeads = ({onAuthError}) => {
    const [leads, setLeads] = useState(null);
    const [filter, setFilter] = useState("NEW");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setError("");
        try {
            setLeads(await listOfficeLeads());
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => { load(); }, [load]);

    const mark = async (lead, status) => {
        setBusy(lead.id);
        setError("");
        try {
            await setOfficeLeadStatus(lead.id.replace("lead-", ""), status);
            await load();
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const shown = (leads || []).filter((l) => filter === "ALL" || l.status === filter);

    return (
        <div>
            <div className="brk-card__head">
                <div className="brk-filters" role="group" aria-label="Filter leads by status">
                    {FILTERS.map((s) => (
                        <button key={s} type="button" aria-pressed={filter === s}
                                className={`brk-filter${filter === s ? " brk-filter--on" : ""}`} onClick={() => setFilter(s)}>
                            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                            <span>{(leads || []).filter((l) => s === "ALL" || l.status === s).length}</span>
                        </button>
                    ))}
                </div>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}

            {!leads ? <p className="brk-empty">Loading…</p> : shown.length === 0 ? <p className="brk-empty">No leads here.</p> : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Buyer</th><th>Listing</th><th>Message</th><th>Received</th><th>Status</th><th/></tr>
                        </thead>
                        <tbody>
                        {shown.map((l) => (
                            <tr key={l.id}>
                                <td>
                                    <div className="brk-strong">{l.name || "—"}</div>
                                    <div className="brk-hint"><a href={`tel:${l.mobile}`}>{l.mobile}</a></div>
                                    {l.email && <div className="brk-hint"><a href={`mailto:${l.email}`}>{l.email}</a></div>}
                                </td>
                                <td>
                                    <div>{l.propertyTitle}</div>
                                    <div className="brk-hint">{KIND[l.kind] || l.kind}</div>
                                </td>
                                <td>{l.message || <span className="brk-hint">—</span>}</td>
                                <td><span className="brk-hint">{when(l.createdAt)}</span></td>
                                <td><span className={`brk-badge brk-badge--${TONE[l.status] || "grey"}`}>{l.status}</span></td>
                                <td>
                                    <span className="brk-row-actions" style={{flexDirection: "column", alignItems: "flex-start"}}>
                                        {l.status !== "CONTACTED" && (
                                            <button type="button" className="brk-link" disabled={busy === l.id}
                                                    onClick={() => mark(l, "CONTACTED")}>Mark contacted</button>
                                        )}
                                        {l.status !== "CLOSED" && (
                                            <button type="button" className="brk-link" disabled={busy === l.id}
                                                    onClick={() => mark(l, "CLOSED")}>Close</button>
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OfficeLeads;
